import { FileText, Loader2, AlertCircle } from 'lucide-react';
import type { CustomToolRenderer } from '@cotera/client/v0/chat/tool-renderers';

const filenameFromInput = (input: Record<string, unknown>): string => {
  const filename = input['filename'];
  return typeof filename === 'string' && filename !== ''
    ? filename
    : 'your file';
};

/**
 * Renders the `coco/upload-file` tool call: the in-chat stand-in for a
 * non-tabular document (pdf/text/markdown/html/pptx) dropped into the chat. Its
 * bytes go straight to the chat's ingot and never enter the model context.
 *
 * While Ingot parses the file the tool call is incomplete and this shows a
 * "processing…" row; `IngestChatFileSaga` then completes it — with a `file`
 * output (ready) or a `failed` note — and the row flips over the chat's live
 * stream. State is read from the tool call's own output.
 */
export const UploadFileToolRenderer: CustomToolRenderer = ({ part }) => {
  const output = part.getOutput<{
    file?: { filename?: string; status?: string };
    failed?: boolean;
  }>();
  const filename = filenameFromInput(part.input);

  // Parse failed: the saga completes the placeholder with `failed`. Checked
  // first because the still-processing branch also has no `file` output.
  if (output?.failed === true) {
    return (
      <div className="-mt-3 mb-4 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 text-sm text-muted-foreground">
          Couldn&apos;t read{' '}
          <span className="font-medium text-foreground">{filename}</span>.
        </div>
      </div>
    );
  }

  // Still processing: the tool call is incomplete and has no `file` output yet.
  if (output?.file === undefined) {
    return (
      <div className="-mt-3 mb-4 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        <div className="min-w-0 text-sm text-muted-foreground">
          Processing{' '}
          <span className="font-medium text-foreground">{filename}</span>…
        </div>
      </div>
    );
  }

  return (
    <div className="-mt-3 mb-4 ml-auto flex w-fit max-w-md items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-left">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{filename}</div>
        <div className="truncate text-xs text-muted-foreground">
          Searchable in this chat
        </div>
      </div>
    </div>
  );
};
